import {
  CashbackProgressResponse,
  DiscountRuleRow,
  EmployeesProgressResponse,
  GetParametersResponse
} from "./interfaces"
import {
  makeCoreDeleteRequest,
  makeCoreGetRequest,
  makeCorePostRequest,
  makeCorePutRequest
} from "../../utils/api"
import { Filter } from "../core-ui/Table/TableDefinitions"
import { normalizeOrder, normalizeOrderBy, sanitize } from "./utils"

export async function fetchParameters(params?: {
  page?: number
  size?: number
  year?: number
  month?: number
}): Promise<GetParametersResponse> {
  const qs = new URLSearchParams()
  if (params?.page != null) qs.set('page', String(params.page))
  if (params?.size != null) qs.set('size', String(params.size))
  if (params?.year != null) qs.set('year', String(params.year))
  if (params?.month != null) qs.set('month', String(params.month))

  const url = `/employee-discount/parameter${qs.toString() ? `?${qs.toString()}` : ''}`
  return makeCoreGetRequest(url)
}

export async function addParameter(payload: Omit<DiscountRuleRow, 'id' | 'day'>): Promise<any> {
  return makeCorePostRequest(`/employee-discount/parameter-add`, payload)
}

export async function updateParameter(payload: Omit<DiscountRuleRow, 'day'>): Promise<any> {
  return makeCorePutRequest(`/employee-discount/parameter-update`, payload)
}

export async function deleteParameter(id): Promise<void> {
  return makeCoreDeleteRequest(`/employee-discount/parameter-delete/${id}`)
}

export const fetchEmployeesProgress = async ({
 year,
 month,
 page,
 size,
 tableFilters
}: {
  year: number
  month: number
  page?: number
  size?: number
  tableFilters?: Filter[]
}): Promise<EmployeesProgressResponse> => {
  const qs = new URLSearchParams()
  qs.set("year", String(year))
  qs.set("month", String(month))
  if (page != null) qs.set("page", String(page))
  if (size != null) qs.set("size", String(size))

  const active = (tableFilters ?? [])
    .map(f => ({ name: f?.name?.trim(), value: String(f?.value ?? "").trim() }))
    .filter(f => f.name && f.value)

  if (active.length) {
    const sanitize = (s: string) => s.replaceAll("|", " ").trim()

    qs.set("filterBy", active.map(f => sanitize(f.name)).join("|"))
    qs.set("filterValue", active.map(f => sanitize(f.value)).join("|"))
  }

  return makeCoreGetRequest(`/employee-discount/employees-progress?${qs.toString()}`)
}

export const fetchCashbackProgressDetails = async ({
 afgCardId,
 year,
 month,
 order,
 orderBy,
 tableFilters
}: {
  afgCardId: string
  year: number
  month: number
  order?: string
  orderBy?: string
  tableFilters?: Filter[]
}): Promise<CashbackProgressResponse> => {
  const qs = new URLSearchParams()

  qs.set("afgCardId", String(afgCardId))
  qs.set("year", String(year))
  qs.set("month", String(month))

  const active = (tableFilters ?? [])
    .map(f => ({
      name: sanitize(f?.name),
      value: sanitize(f?.value),
    }))
    .filter(f => f.name && f.value)

  if (active.length) {
    qs.set("filterBy", active.map(f => f.name).join("|"))
    qs.set("filterValue", active.map(f => f.value).join("|"))
  }

  const ob = normalizeOrderBy(orderBy)
  const o = normalizeOrder(order)

  if (ob && o) {
    qs.set("orderBy", ob)
    qs.set("order", o)
  }

  return makeCoreGetRequest(`/employee-discount/cashback-progress?${qs.toString()}`)
}

export const fetchEligibilityRules = (params?: {
  page?: number
  size?: number
  year?: number
  month?: number
}) => {
  const qs = new URLSearchParams()

  if (params?.page !== undefined) qs.set('page', String(params.page))
  if (params?.size !== undefined) qs.set('size', String(params.size))
  if (params?.year !== undefined) qs.set('year', String(params.year))
  if (params?.month !== undefined) qs.set('month', String(params.month))

  const url = qs.toString() ? `/employee-discount/parameter?${qs.toString()}` : `/parameter`
  return makeCoreGetRequest(url)
}
